import LegalDocument, { LegalSection } from '@/components/legal-document';

const CONTACT_EMAIL = 'lance.foley@caprocklabs.com';
const TEXT_LINK =
  'underline decoration-accent underline-offset-4 hover:decoration-foreground rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring';

interface Recipient {
  name: string;
  purpose: string;
  receives: string;
}

// Each outside service that can receive data, and what it gets. Keep this in
// step with the providers in app/lib/platform/provider.rb.
const RECIPIENTS: Recipient[] = [
  {
    name: 'Render',
    purpose: 'Runs the app and its main database',
    receives: 'All data that the app stores',
  },
  {
    name: 'Supabase',
    purpose: 'A database of its own for each Build and Studio app',
    receives: 'The lists and access rules of those apps',
  },
  {
    name: 'Cloudflare R2',
    purpose: 'Stores attachments, with one storage area for each app',
    receives: 'Files that you or your team upload',
  },
  {
    name: 'Resend',
    purpose: 'Sends invoices, receipts, and account email',
    receives: 'The email address, the subject, and the message',
  },
  {
    name: 'Stripe',
    purpose: 'Bills your plan. Takes payments in apps that you connect',
    receives: 'Your billing contact, and the invoices that your app sends',
  },
  {
    name: 'Square',
    purpose: 'Takes payments in apps that you connect',
    receives: 'The invoices that your app sends',
  },
  {
    name: 'Intuit QuickBooks',
    purpose: 'Your books, when you connect them',
    receives: 'The invoices and payments that you choose to send',
  },
  {
    name: 'OpenRouter',
    purpose: 'Writes a first draft of an app design',
    receives: 'The description that you type, and the names of your lists and fields',
  },
  {
    name: 'Sentry',
    purpose: 'Reports errors in the app',
    receives: 'The error, and the id and email of the person who was logged in',
  },
];

interface Props {
  updatedOn: string;
}

const Show = ({ updatedOn }: Props) => {
  return (
    <LegalDocument
      eyebrow="Privacy"
      title="Privacy policy"
      updatedOn={updatedOn}
      summary={
        <p>
          CAP Rock Labs keeps the data that it needs to run your account and your apps. We do not
          sell data. We do not see card numbers: your customers pay on Stripe or Square pages. The
          data inside your apps belongs to you, and we use it only to run those apps.
        </p>
      }
    >
      <LegalSection number={1} title="Who we are">
        <p>
          CAP Rock Labs is a software company in Litchfield, Minnesota. This policy covers the
          website, the console where you build apps, and the apps that you publish with us.
        </p>
        <p>
          For questions, email{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className={TEXT_LINK}>
            {CONTACT_EMAIL}
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection number={2} title="Two kinds of data">
        <ul>
          <li>
            <strong>Your account data.</strong> Your name, your email, and how you use CAP Rock
            Labs. We decide how this data is used, and this policy says how.
          </li>
          <li>
            <strong>Data inside your apps.</strong> Your records, customers, invoices, attachments,
            and messages. You decide what goes in and who sees it. We store and process it only to
            run your apps for you.
          </li>
        </ul>
      </LegalSection>

      <LegalSection number={3} title="What we collect">
        <ul>
          <li>
            <strong>Account.</strong> Your name, email address, company name, and role. Your
            password is kept only as a one-way hash, so nobody can read it.
          </li>
          <li>
            <strong>Logins.</strong> Each login records the IP address and the browser that you
            used.
          </li>
          <li>
            <strong>Visits.</strong> When you open the site, we record a visit: the IP address,
            browser, device, the page you came from, and an approximate location.
          </li>
          <li>
            <strong>Plan and usage.</strong> Your plan, your subscription status, and how many
            emails, build credits, file space, and record space your apps use.
          </li>
          <li>
            <strong>App content.</strong> The lists, records, attachments, customers, invoices, and
            messages in your apps, and a log of the email each app sends.
          </li>
          <li>
            <strong>Connections.</strong> When you connect Stripe, Square, or QuickBooks, we keep
            the account id and the access tokens. The tokens are encrypted when they are stored.
          </li>
        </ul>
        <p>
          We do not collect card numbers. Stripe holds the card for your plan. Your customers pay on
          Stripe or Square pages.
        </p>
      </LegalSection>

      <LegalSection number={4} title="Cookies">
        <p>
          The site uses cookies to keep you logged in, to remember your screen settings, and to
          count visits. We do not use advertising cookies.
        </p>
      </LegalSection>

      <LegalSection number={5} title="Who receives data">
        <p>These services run parts of CAP Rock Labs. Each one gets only what its job needs.</p>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[32rem] text-left text-sm">
            <thead>
              <tr className="border-b">
                <th className="py-2 pr-4 font-medium">Service</th>
                <th className="py-2 pr-4 font-medium">What it does</th>
                <th className="py-2 font-medium">What it receives</th>
              </tr>
            </thead>
            <tbody>
              {RECIPIENTS.map((recipient) => (
                <tr key={recipient.name} className="border-b align-top">
                  <td className="py-2 pr-4 font-medium">{recipient.name}</td>
                  <td className="text-muted-foreground py-2 pr-4">{recipient.purpose}</td>
                  <td className="text-muted-foreground py-2">{recipient.receives}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          The draft writer gets no records, no customers, and no amounts. Some free AI models keep
          the text that they receive. Do not put personal or payment details in an app description.
        </p>
      </LegalSection>

      <LegalSection number={6} title="Money and books">
        <p>
          We do not move money or send data to your books by ourselves. A pay link is made when you
          send an invoice. An invoice goes to QuickBooks only when a person on your team clicks Send
          to QuickBooks.
        </p>
      </LegalSection>

      <LegalSection number={7} title="How we protect data">
        <ul>
          <li>Every page uses HTTPS.</li>
          <li>Passwords are hashed. Connection tokens are encrypted.</li>
          <li>Each app can read only its own records.</li>
          <li>Only CAP Rock Labs staff can open the staff pages.</li>
        </ul>
      </LegalSection>

      <LegalSection number={8} title="Your choices">
        <p>
          Email us to see, correct, or delete your data. When you close your account, we delete your
          apps and their data. We keep billing records for as long as the law requires.
        </p>
      </LegalSection>

      <LegalSection number={9} title="Your customers">
        <p>
          When your app collects data from your customers, you are responsible for telling them and
          for your right to collect it. We process that data for you, as described here.
        </p>
      </LegalSection>

      <LegalSection number={10} title="Children">
        <p>CAP Rock Labs is for businesses. It is not for children under 13.</p>
      </LegalSection>

      <LegalSection number={11} title="Changes to this policy">
        <p>
          When this policy changes, we update this page and the date at the top. For a large change,
          we also email account owners.
        </p>
      </LegalSection>
    </LegalDocument>
  );
};

export default Show;
